import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { setClerkAuthInfo, clearClerkAuthInfo } from '../../store/defaultBaseQuery';
import { useGetUserAttributesQuery } from '../../store/userApi';
import { useDispatch } from 'react-redux';
import { setUser, clearUser } from '../../store/userSlice';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Componente que maneja la integración entre Clerk y el backend
 * 
 * Este componente:
 * 1. Obtiene el token JWT de Clerk cuando el usuario está autenticado
 * 2. Configura el token para todas las solicitudes API
 * 3. Sincroniza la información del usuario con el estado de Redux
 * 4. Redirige al dashboard cuando el usuario ya tiene sesión (excepto en flujos específicos)
 */
const ClerkAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthSetupComplete, setIsAuthSetupComplete] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Lista de rutas donde no queremos redirección automática al dashboard
  const excludedRedirectPaths = [
    '/subscription/pro', 
    '/direct-pro-signup',
    '/signup/verify',
    '/subscription/',  // Any subscription-related route
    '/checkout/',      // Any checkout-related route
    '/payment/',       // Any payment-related route
    '/billing/'        // Any billing-related route
  ];
  
  // Consulta para obtener los atributos del usuario del backend
  const { 
    data: userAttributes, 
    refetch, 
    error: userAttributesError, 
    isLoading,
    isSuccess,
    isUninitialized
  } = useGetUserAttributesQuery(undefined, {
    // Solo llamar a la API cuando el usuario esté autenticado Y el token esté configurado
    skip: !isSignedIn || !isAuthSetupComplete || !(window as any).__clerk_jwt,
    // Refrescar los datos cada 5 minutos para mantener la sesión activa
    pollingInterval: 5 * 60 * 1000,
    // No usar caché para esta consulta
    refetchOnMountOrArgChange: true,
  });

  // Helper function to safely call refetch only when the query has been started
  const safeRefetch = useCallback(() => {
    if (!isUninitialized && isSignedIn && isAuthSetupComplete && (window as any).__clerk_jwt) {
      console.log('[Auth] Safely refetching user data');
      refetch();
    } else {
      console.log('[Auth] Skipping refetch - query not started or conditions not met', {
        isUninitialized,
        isSignedIn,
        isAuthSetupComplete,
        hasClerkJwt: !!(window as any).__clerk_jwt
      });
    }
  }, [isUninitialized, isSignedIn, isAuthSetupComplete, refetch]);

  // Efecto para redirigir al dashboard cuando hay una sesión activa
  useEffect(() => {
    // Si el usuario tiene sesión, está en una página de login/signup pero no en un flujo especial
    if (isSignedIn && isAuthSetupComplete && userAttributes) {
      const currentPath = location.pathname;
      const isLoginOrSignup = currentPath === '/login' || 
                              currentPath === '/signup' || 
                              currentPath.startsWith('/login/') || 
                              currentPath.startsWith('/signup/');
      
      // Verificar si estamos en una ruta excluida de redirección
      const isExcludedPath = excludedRedirectPaths.some(path => 
        currentPath.startsWith(path)
      );
      
      // Si estamos en login/signup pero no en una ruta excluida, redirigir al dashboard
      if (isLoginOrSignup && !isExcludedPath) {
        console.log('[Auth] Active session detected on login/signup page. Redirecting to dashboard.');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isSignedIn, isAuthSetupComplete, userAttributes, location.pathname, navigate]);

  // Efecto para limpiar la información cuando el usuario cierra sesión
  useEffect(() => {
    if (!isSignedIn && isAuthSetupComplete) {
      clearClerkAuthInfo();
      dispatch(clearUser());
      console.log('[Auth] User signed out, cleared auth info');
    }
  }, [isSignedIn, isAuthSetupComplete, dispatch]);

  // Efecto para configurar los tokens cuando el usuario inicia sesión o cambia
  useEffect(() => {
    const setupAuth = async () => {
      if (isSignedIn && user) {
        try {
          setAuthError(null);
          
          // Obtener el token JWT de Clerk
          const token = await getToken();
          
          if (!token) {
            console.error('[Auth] No token available from Clerk');
            setAuthError('No authentication token available');
            return;
          }
          
          // Guardar los datos de autenticación para las solicitudes de API
          setClerkAuthInfo(
            token, 
            user.id,
            user.primaryEmailAddress?.emailAddress
          );
          
          console.log('[Auth] Clerk token set successfully:', {
            userId: user.id,
            email: user.primaryEmailAddress?.emailAddress
          });
          
          setIsAuthSetupComplete(true);
          
          // Note: safeRefetch will be called automatically when the query conditions are met
          // due to the useEffect hook and skip conditions changing
        } catch (error) {
          console.error('[Auth] Error setting Clerk token:', error);
          setAuthError('Error during authentication setup');
        }
      } else if (!isSignedIn) {
        // Si el usuario no está autenticado, marcar como completado para no bloquear la UI
        setIsAuthSetupComplete(true);
      }
    };
    
    setupAuth();
  }, [isSignedIn, user, getToken, safeRefetch]);

  // Effect to trigger initial data fetch when auth setup is complete
  useEffect(() => {
    if (isSignedIn && isAuthSetupComplete && (window as any).__clerk_jwt && isUninitialized) {
      console.log('[Auth] Auth setup complete, triggering initial user data fetch');
      safeRefetch();
    }
  }, [isSignedIn, isAuthSetupComplete, isUninitialized, safeRefetch]);
  
  // Efecto para sincronizar los datos del usuario del backend con Redux
  useEffect(() => {
    if (isSuccess && userAttributes) {
      dispatch(setUser(userAttributes));
      console.log('[Auth] User data synced with backend:', {
        id: userAttributes.id,
        email: userAttributes.email,
        plan: userAttributes.plan || 'Unknown',
        hasActiveSubscription: userAttributes.hasActiveSubscription
      });
    } else if (userAttributesError) {
      console.error('[Auth] Error fetching user data:', userAttributesError);
      
      // If there's an error, but we have Clerk user information, create a minimal user object
      // This ensures the app can still function even when the backend fails
      if (isSignedIn && user) {
        console.log('[Auth] Creating fallback user object from Clerk data');
        const fallbackUser = {
          id: user.id,
          email: user.primaryEmailAddress?.emailAddress || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          plan: 'FREE', // Default to FREE plan
          hasActiveSubscription: false,
          isVerified: true,
          role: 'USER',
          prompts: []
        };
        
        // Set the fallback user in Redux
        dispatch(setUser(fallbackUser as any));
      }
      
      // Si hay un error 401 (No autorizado), puede ser que la sesión haya expirado
      if (userAttributesError.toString().includes('401')) {
        console.log('[Auth] Session expired or invalid, refreshing token');
        // Intentar refrescar el token
        const refreshAuth = async () => {
          if (isSignedIn && user) {
            try {
              const newToken = await getToken({ skipCache: true });
              if (newToken) {
                setClerkAuthInfo(
                  newToken, 
                  user.id,
                  user.primaryEmailAddress?.emailAddress
                );
                console.log('[Auth] Token refreshed');
                // Intentar obtener los datos de usuario de nuevo
                safeRefetch();
              }
            } catch (e) {
              console.error('[Auth] Failed to refresh token:', e);
            }
          }
        };
        refreshAuth();
      }
    }
  }, [userAttributes, userAttributesError, isSuccess, dispatch, isSignedIn, user, getToken, safeRefetch]);

  // Si hay un error de autenticación, mostrarlo
  if (authError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="body1">
          Authentication error: {authError}. Please try refreshing the page.
        </Typography>
      </Box>
    );
  }
  
  // Si estamos cargando datos del usuario y el usuario está autenticado, mostrar spinner
  if (isSignedIn && isAuthSetupComplete && isLoading && !userAttributes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return <>{children}</>;
};

export default ClerkAuthProvider; 